import { InventoryStatus } from '@adapters/bigcommerce/types/product';
import { buildSPUHoursMessaging } from '@utils/store/buildSPUHoursMessaging';
import {
  FEATURE_FLAG_DISABLE_SPU,
  FEATURE_FLAG_NEXT_DAY,
  FEATURE_FLAG_NO_PICKUP_TIMES,
} from '@config/feature-flag';
import { useStoresUI } from '@context/StoresContext';
import { useTranslation } from 'next-i18next';
import { usePdp } from '@context/PdpContext';

export function useStoreAvailability(
  storeId: string,
  pickupMessage: string | null,
  locale: string,
  quantity: number | null
) {
  const { favouriteStoreDetails } = useStoresUI();
  const { t } = useTranslation('pdp');
  const { selectedAvailability, selectedVariant, shippingRestrictions } =
    usePdp();

  let stockIcon;
  let stockMessage;
  let availabilityMessage;
  let outOfStockMessage;

  const hasSelectedVariant = !!selectedVariant;
  if (!hasSelectedVariant) return null;

  const storeQuantity = selectedVariant?.storeInventory[storeId]?.quantity;
  const minPurchaseQuantityMet =
    typeof storeQuantity === 'number' &&
    typeof quantity === 'number' &&
    storeQuantity >= quantity;

  const storeInStock =
    !!selectedVariant?.storeInventory[storeId]?.status &&
    !(
      selectedVariant?.storeInventory[storeId]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[storeId]?.status ===
        InventoryStatus.NOT_STOCKED
    ) &&
    minPurchaseQuantityMet;

  const storeLowStock =
    selectedVariant?.storeInventory[storeId]?.quantity > 0 &&
    selectedVariant?.storeInventory[storeId]?.quantity < 5;

  const storeOutOfStock =
    selectedVariant?.storeInventory[storeId]?.status ===
    InventoryStatus.OUT_OF_STOCK;

  const stsDCInStock =
    !!selectedVariant?.storeInventory[99]?.status &&
    !(
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.OUT_OF_STOCK ||
      selectedVariant?.storeInventory[99]?.status ===
        InventoryStatus.NOT_STOCKED
    );

  const stsShopInStock = !!selectedVariant?.hasStockAvailable;
  const stsInStock = stsDCInStock || stsShopInStock;

  const isAvailableInOtherStores = () => {
    if (!selectedVariant?.storeInventory) {
      return false;
    }

    // Check all stores except store 99 (distribution centre)
    return Object.entries(selectedVariant.storeInventory).some(
      ([mecStoreId, inventory]) => {
        // Skip store 99 (distribution centre)
        if (mecStoreId === '99' || mecStoreId === '100') {
          return false;
        }

        // Check if the store has inventory available
        return (
          inventory.status === InventoryStatus.IN_STOCK ||
          inventory.status === InventoryStatus.LOW_STOCK
        );
      }
    );
  };

  const availableInOtherStores = isAvailableInOtherStores()
    ? {
        start: t(
          'product.free.store.pickup.new.available.in.other.stores.start'
        ),
        end: t('product.free.store.pickup.new.available.in.other.stores.end'),
      }
    : null;

  if (!pickupMessage && favouriteStoreDetails) {
    const spuMessageObj = buildSPUHoursMessaging(favouriteStoreDetails, t);
    pickupMessage = t(`pdp:product.pickup.short.${spuMessageObj.key}`, {
      day: spuMessageObj.day,
    });
  }

  if (selectedAvailability) {
    // Store Specific Messaging
    if (storeInStock && !storeLowStock) {
      stockMessage = t('product.in.stock');
    } else if (storeInStock && storeLowStock) {
      stockMessage = t('product.low.stock');
    } else if (!storeInStock && stsInStock) {
      stockMessage = t('product.sts.stocked');
    } else if (storeOutOfStock) {
      stockMessage = t('product.out.of.stock');
    }
  }

  if (process.env.NEXT_PUBLIC_ENABLE_LOGGING === 'true') {
    console.log({
      storeInStock,
      stsInStock,
      lookingFor: !storeInStock && stsInStock,
    });
  }
  // SPU / STS Messaging
  if (
    !selectedAvailability ||
    FEATURE_FLAG_DISABLE_SPU ||
    FEATURE_FLAG_NO_PICKUP_TIMES ||
    !selectedVariant?.quickPickup
  ) {
    // Store Specific Messaging For store only products
    if (storeInStock && storeLowStock) {
      stockMessage = t('product.low.stock');
      availabilityMessage = 'SPU';
    } else if (
      storeOutOfStock &&
      shippingRestrictions?.shipping_allowed === true
    ) {
      stockMessage = t('product.out.of.stock');
      availabilityMessage = t('product.pickup.out.of.stock');
    } else if (
      !storeInStock &&
      stsInStock &&
      shippingRestrictions?.shipping_allowed === false
    ) {
      stockIcon = 'outOfStock';
      stockMessage = t('product.out.of.stock');
      outOfStockMessage = {
        start:
          locale === 'fr'
            ? t('product.pickup.out.of.stock.start.fr')
            : t('product.pickup.out.of.stock.start'),
        storeName: favouriteStoreDetails?.data?.store_name,
        end: locale === 'en' ? t('product.pickup.out.of.stock.end') : '',
      };
      availabilityMessage = t('product.out.of.stock');
    } else if (storeInStock) {
      availabilityMessage = 'SPU';
    }
  } else if (storeInStock) {
    availabilityMessage = FEATURE_FLAG_NEXT_DAY ? 'SPU' : pickupMessage;
  } else if (!storeInStock && stsInStock) {
    availabilityMessage = `STS`;
  } else if (!storeInStock && !stsInStock) {
    stockIcon = 'outOfStock';
    stockMessage = t('product.out.of.stock');
    availabilityMessage = t('product.pickup.out.of.stock');
  }

  const calculateStockLevel = () => {
    const storeInventory = selectedVariant?.storeInventory[storeId]?.quantity;

    if (storeInventory === 0) {
      return 'outOfStock';
    }
    if (storeInventory && storeInventory < 5) {
      return 'low';
    }
    if (storeInventory && storeInventory >= 5 && storeInventory <= 10) {
      return 'medium';
    }
    if (storeInventory && storeInventory > 10) {
      return 'high';
    }
    return false;
  };

  const getStockLevelMessage = () => {
    if (calculateStockLevel() === 'outOfStock') {
      return t('product.out.of.stock');
    }
    if (calculateStockLevel() === 'low') {
      return t('product.low.stock.message', {
        stock: selectedVariant?.storeInventory[storeId]?.quantity,
      });
    }
    if (
      calculateStockLevel() === 'medium' ||
      calculateStockLevel() === 'high'
    ) {
      return t('product.in.stock');
    }
  };

  const stockLevel = calculateStockLevel() || null;
  const stockLevelMessage = getStockLevelMessage() || null;

  return {
    stockIcon,
    stockMessage,
    availabilityMessage,
    outOfStockMessage,
    stockLevel,
    stockLevelMessage,
    availableInOtherStores,
  };
}
